import { BaseOptionType, DefaultOptionType } from 'antd/es/select';
import { isDefined } from 'class-validator';
import dayjs from 'dayjs';
import { ReactNode } from 'react';
import { InterestClassType, InterestsTypes } from 'src/enums/interests.enum';
import { SensorType } from 'src/enums/nginr-type.enum';
import { RecipientEnum, RuleEnum, SemanticValidatorType } from 'src/enums/rule.enum';
import { EmailPeriod } from 'src/enums/sc3-type.enum';
import { IdValuePairType } from 'src/types/interests.type';
import { RecipientsArray, Rule } from 'src/types/rule.type';

export enum DaysEnum {
  Sunday,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
}

export const convertFormValuesToPayload = (values: any, recordId?: string): any => {
  const recipients: RecipientsArray[] = [];

  (['webReceivers', 'smsReceivers', 'to', 'bcc', 'cc'] as const).forEach((field) => {
    if (values[field]) {
      const type = getTypeOfField(field);
      if (type === -1) return;
      recipients.push(
        ...values[field].map((opt: BaseOptionType) => ({
          recipientId: opt.value,
          type,
        })),
      );
    }
  });

  return {
    id: recordId || undefined,
    subject: values.subject,
    expiresAt: values.expirationDate,
    activationDate: values.activationDate,
    isActive: recordId ? values.isActive : undefined,
    type: values.type,
    semanticValidatorType: values.semanticValidator,
    isRNA: values.isRNA,
    sound: values.sound,
    // @ts-ignore // TODO: neden hata veriyor ?
    // elementsOfRule: !recordId ? elementsOfRule : undefined,
    recipients,
    emailPeriod: values.emailNotification ? values.emailPeriod : EmailPeriod.Never,
    // isActive: recordId ? values.isActive : undefined,
    firstTriggerDate: values.firstTriggerDate ? dayjs(values.firstTriggerDate).unix() : null,
    activeBetweenHours: values.activeHours
      ? {
          startTime: values.activeHours[0] ? dayjs(values.activeHours[0]).format('HH:mm') : null,
          endTime: values.activeHours[1] ? dayjs(values.activeHours[1]).format('HH:mm') : null,
        }
      : null,
    activeDays: values.activeDays,
  };
};

export const convertRuleRecordToEditFormData = (
  rule: Rule,
  // sensorList: SensorIdentifiedDataType[],
  // interestList: IdValuePairType[]
) => {
  const sensors: DefaultOptionType[] = [];
  // rule.elementsOfRule.forEach((el) => {
  //   el.elementOfRuleSensors?.forEach((sensor) => {
  //     const foundSensor = sensorList.find((obj) => obj.id === sensor.sensorId);
  //     if (foundSensor)
  //       sensors.push({
  //         value: foundSensor.id,
  //         label: foundSensor.name,
  //       });
  //   });
  // });
  const to: DefaultOptionType[] = [];
  const bcc: DefaultOptionType[] = [];
  const cc: DefaultOptionType[] = [];
  const webReceivers: DefaultOptionType[] = [];
  const smsReceivers: DefaultOptionType[] = [];

  rule.recipients.forEach((recipient) => {
    const user = recipient.users;
    const option = {
      // label: recipient.type === RecipientEnum.WEB_RECEIVER ? user.fullname : user.email,
      label: user.fullname,
      value: user.id,
      title: recipient.type === RecipientEnum.WEB_RECEIVER ? user.email : undefined,
    };
    switch (recipient.type) {
      case RecipientEnum.BCC:
        bcc.push(option);
        break;
      case RecipientEnum.CC:
        cc.push(option);
        break;
      case RecipientEnum.TO:
        to.push(option);
        break;
      case RecipientEnum.WEB_RECEIVER:
        webReceivers.push(option);
        break;
      case RecipientEnum.SMS:
        smsReceivers.push(option);
        break;
      default:
        break;
    }
  });

  return {
    id: rule.id,
    subject: rule.subject,
    expirationDate: rule.expiresAt ? dayjs(rule.expiresAt) : undefined,
    activationDate: rule.activationDate ? dayjs(rule.activationDate) : undefined,
    isActive: rule.isActive,
    sensors,
    webReceivers,
    smsReceivers,
    to,
    bcc,
    cc,
    sms: !!smsReceivers.length,
    emailNotification: isDefined(rule.emailPeriod) && rule.emailPeriod !== EmailPeriod.Never,
    emailPeriod: rule.emailPeriod === EmailPeriod.Never ? undefined : rule.emailPeriod,
    firstTriggerDate: rule.firstTriggerDate ? dayjs(rule.firstTriggerDate * 1000) : undefined,
    isScenario: rule.type === RuleEnum.SCENARIO,
    semanticValidator: rule.semanticValidatorType,
    isRNA: rule.isRNA,
    sound: rule.sound,
    activeHours: rule.startTime && rule.endTime ? [dayjs(rule.startTime, 'HH:mm'), dayjs(rule.endTime, 'HH:mm')] : null,
    activeDays: rule.activeDays,
    // interestType: interestTypes,
    // interestId: interestIds,
  };
};

export const getTypeOfField = (
  fieldName: 'bcc' | 'cc' | 'to' | 'smsReceivers' | 'webReceivers',
): RecipientEnum | number => {
  switch (fieldName) {
    case 'bcc':
      return RecipientEnum.BCC;
    case 'cc':
      return RecipientEnum.CC;
    case 'to':
      return RecipientEnum.TO;
    case 'webReceivers':
      return RecipientEnum.WEB_RECEIVER;
    case 'smsReceivers':
      return RecipientEnum.SMS;
    default:
      return -1;
  }
};

export type EstimationState = {
  dailyEstimation: number;
  weeklyEstimation: number;
  isFetching: boolean;
  isEstimateHidden: boolean;
  estimateAgain: boolean;
};

export const defaultEstimationState: EstimationState = {
  dailyEstimation: 0,
  weeklyEstimation: 0,
  isFetching: false,
  isEstimateHidden: true,
  estimateAgain: false,
};

export type RuleConditionNodeType = {
  [InterestClassType.Identified]?: {
    render: () => ReactNode;
  };
  [InterestClassType.Recognized]?: {
    render: () => ReactNode;
  };
  filter?: (options: IdValuePairType[], selectedValues?: DefaultOptionType[]) => DefaultOptionType[];
};

export type RuleConditionsType = {
  [sensorType in keyof typeof SensorType]?: {
    [interestType in keyof typeof InterestsTypes]?: RuleConditionNodeType;
  };
};

export type SemanticValidatorRuleContiditionsType = {
  [semanticValidatorType in keyof typeof SemanticValidatorType]?: {
    [interestType in keyof typeof InterestsTypes]?: RuleConditionNodeType;
  };
};

export const SEMANTIC_VALIDATOR_RULES: SemanticValidatorRuleContiditionsType = {
  // UMP: {
  //   Person: {
  //     filter: (interests, selectedValues) => {
  //       return interests.map((int) => {
  //         return {
  //           title: int.type.toString(),
  //           label: int.value,
  //           value: int.id,
  //           disabled:
  //             int.classType === InterestClassType.Recognized
  //               ? true
  //               : selectedValues
  //                 ? selectedValues?.length > 0
  //                 : false,
  //         };
  //       });
  //     },
  //   },
  //   Vehicle: {
  //     filter: (interests, selectedValues) => {
  //       return interests.map((int) => {
  //         return {
  //           title: int.type.toString(),
  //           label: int.value,
  //           value: int.id,
  //           disabled:
  //             int.classType === InterestClassType.Recognized
  //               ? true
  //               : selectedValues
  //                 ? selectedValues?.length > 0
  //                 : false,
  //         };
  //       });
  //     },
  //   },
  // },
  Height_SV: {
    Person: {
      filter: (interests, selectedValues) => {
        return interests.map((int) => {
          return {
            title: int.type.toString(),
            label: int.value,
            value: int.id,
            disabled:
              int.classType === InterestClassType.Recognized
                ? true
                : selectedValues
                  ? selectedValues?.length > 0
                  : false,
          };
        });
      },
    },
  },
  Vehicle_SV: {
    Vehicle: {
      filter: (interests, selectedValues) => {
        return interests.map((int) => {
          return {
            title: int.type.toString(),
            label: int.value,
            value: int.id,
            disabled:
              int.classType === InterestClassType.Recognized
                ? true
                : selectedValues
                  ? selectedValues?.length > 0
                  : false,
          };
        });
      },
    },
  },
  Distance_SV: {
    Person: {
      filter: (interests, selectedValues) => {
        return interests.map((int) => {
          return {
            title: int.type.toString(),
            label: int.value,
            value: int.id,
            disabled:
              int.classType === InterestClassType.Recognized
                ? true
                : selectedValues
                  ? selectedValues?.length > 0
                  : false,
          };
        });
      },
    },
    Vehicle: {
      filter: (interests, selectedValues) => {
        return interests.map((int) => {
          return {
            title: int.type.toString(),
            label: int.value,
            value: int.id,
            disabled:
              int.classType === InterestClassType.Recognized
                ? true
                : selectedValues
                  ? selectedValues?.length > 0
                  : false,
          };
        });
      },
    },
  },
  Financial_SV: {
    Transaction: {
      filter: (interests, selectedValues) => {
        return interests.map((int) => {
          return {
            title: int.type.toString(),
            label: int.value,
            value: int.id,
            disabled:
              int.classType === InterestClassType.Recognized
                ? true
                : selectedValues
                  ? selectedValues?.length > 0
                  : false,
          };
        });
      },
    },
  },
};

export const RULE_CONDITIONS: RuleConditionsType = {
  Card_Reader: {
    Person: {
      filter: (interests) => {
        return interests.map((int) => {
          return {
            title: int.type.toString(),
            label: int.value,
            value: int.id,
            disabled: int.classType === InterestClassType.Recognized,
          };
        });
      },
    },
  },

  Fence_Node: {
    Incident: {
      filter: (interests) => {
        return interests.map((int) => {
          return {
            title: int.type.toString(),
            label: int.value,
            value: int.id,
            // TODO: Incident'lar hazır olduktan sonra tekrar bak
            // disabled: ![
            //   IncidentType.Alarm,
            //   IncidentType.Connection_Lost,
            //   IncidentType.Steady,
            //   IncidentType.Battery_Low,
            //   IncidentType.Power_Source_Battery,
            // ].includes(int.interest?.type),
          };
        });
      },
    },
  },
};
